<template>
  <div class="flex flex-col min-h-0 pb-24">

    <BasePageHeader
      :title="isNew ? $t('transfers.newTitle') : (transfer?.name ?? $t('transfers.singleTitle'))"
      @back="$router.back()"
    >
      <template #actions>
        <span v-if="transfer" class="text-xs font-semibold px-1.5 py-0.5 rounded" :style="statusBadgeStyle(transfer.status)">
          {{ transferStatusLabel(transfer.status, transfer.status_display) }}
        </span>
      </template>
    </BasePageHeader>
    <BaseTaskHelp task="transfers" class="mx-4 mt-3" />

    <!-- Loading -->
    <div v-if="loading" class="px-4 pt-6 space-y-3">
      <div v-for="i in 4" :key="i" class="h-14 rounded-xl bg-white/5 animate-pulse" />
    </div>

    <!-- Load error -->
    <div v-else-if="loadError" class="flex flex-col items-center justify-center py-20 px-8 text-center">
      <p class="text-red-400 font-medium">{{ loadError }}</p>
    </div>

    <template v-else>

      <!-- ── DRAFT EDIT FORM (new or editing existing draft) ── -->
      <div v-if="isNew || transfer?.status === STATUS.DRAFT" class="px-4 pt-5 flex flex-col gap-4">

        <!-- Name -->
        <BaseFormField :label="$t('transfers.nameLabel')" required>
          <BaseInput v-model="form.name" :placeholder="$t('transfers.nameLabel')" />
        </BaseFormField>

        <!-- Destination warehouse -->
        <BaseFormField :label="$t('transfers.destinationWarehouseLabel')" required>
          <BaseSelect v-model="form.destination_warehouse">
            <option :value="null" disabled>{{ $t('transfers.selectWarehouseOption') }}</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ warehouseLabel(w) }}</option>
          </BaseSelect>
        </BaseFormField>

        <!-- Destination area (optional, filtered by selected warehouse) -->
        <BaseFormField :label="$t('transfers.destinationAreaLabel')" :hint="$t('transfers.optionalAreaHint')">
          <BaseSelect v-model="form.destination_area" :disabled="!form.destination_warehouse">
            <option :value="null">{{ $t('transfers.noAreaSpecified') }}</option>
            <option v-for="a in draftAreas" :key="a.id" :value="a.id">{{ a.name }}</option>
          </BaseSelect>
        </BaseFormField>

        <!-- Notes -->
        <BaseFormField :label="$t('common.notes')">
          <BaseTextarea v-model="form.notes" :rows="2" :placeholder="$t('transfers.notesPlaceholder')" />
        </BaseFormField>

        <!-- Divider -->
        <div style="border-top:1px solid rgba(255,255,255,0.06);" />

        <!-- Items header -->
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-gray-200">{{ $t('transfers.itemsLabel', { n: pendingItems.length }) }}</span>
          <span class="text-xs text-gray-500">{{ $t('transfers.selectedCount', { n: pendingItems.length }) }}</span>
        </div>

        <!-- Item search -->
        <BaseFormField :label="$t('transfers.searchItemPlaceholder')">
          <SearchField
            v-model="itemQuery"
            :results="mappedItemSuggestions"
            :debounce="300"
            search-key="transfer-items"
            :placeholder="$t('transfers.searchItemPlaceholder')"
            @search="doItemSearch"
            @select="addInstance($event.meta)"
          />
        </BaseFormField>

        <!-- Pending items list (new transfer, pre-create) -->
        <ul v-if="isNew && pendingItems.length" class="space-y-2">
          <li v-for="inst in pendingItems" :key="inst.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-xl"
            style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-100 truncate">{{ inst.item?.name ?? $t('common.unknown') }}</div>
              <div class="text-xs text-gray-400">{{ inst.assetTag ?? inst.serial_number }}</div>
            </div>
            <button @click="removePending(inst.id)" class="text-gray-500 hover:text-red-400 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </li>
        </ul>

        <!-- Draft lines (existing transfer) -->
        <ul v-if="!isNew && transfer?.lines?.length" class="space-y-2">
          <li v-for="line in transfer.lines" :key="line.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-xl"
            style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-100 truncate">{{ line.item_metadata?.item?.name ?? $t('common.unknown') }}</div>
              <div class="text-xs text-gray-400">{{ line.item_metadata?.assetTag ?? line.item_metadata?.serial_number }}
                <span v-if="line.source_area?.name"> · {{ $t('transfers.fromArea', { area: line.source_area.name }) }}</span>
              </div>
            </div>
            <button @click="removeLine(line.id)" :disabled="saving" class="text-gray-500 hover:text-red-400 transition-colors disabled:opacity-40">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </li>
        </ul>
        <p v-else-if="!isNew" class="text-xs text-gray-500 text-center py-2">{{ $t('transfers.noItemsYet') }}</p>

        <!-- Actions -->
        <div class="flex gap-3 mt-2">
          <button v-if="!isNew" @click="doCancel" :disabled="saving"
            class="flex-1 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            style="background:#1f2937; color:#f87171;">
            {{ $t('transfers.cancelTransfer') }}
          </button>
          <button v-if="isNew" @click="createDraft" :disabled="saving || !form.name || !form.destination_warehouse || !pendingItems.length"
            class="flex-1 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            style="background:#2563eb; color:#fff;">
            {{ saving ? $t('transfers.creating') : $t('transfers.createDraft') }}
          </button>
          <button v-if="!isNew" @click="saveDraft" :disabled="saving || !form.name || !form.destination_warehouse"
            class="py-3 px-4 rounded-xl text-sm font-semibold disabled:opacity-40"
            style="background:#374151; color:#e5e7eb;">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
          <button v-if="!isNew" @click="doDispatch" :disabled="saving || !transfer?.lines?.length"
            class="flex-1 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            style="background:#15803d; color:#fff;">
            {{ saving ? $t('transfers.dispatching') : $t('transfers.dispatch') }}
          </button>
        </div>

      </div>

      <!-- ── DISPATCHED / PARTIALLY RECEIVED ── -->
      <div v-else-if="transfer?.status === STATUS.DISPATCHED || transfer?.status === STATUS.PARTIALLY_RECEIVED" class="px-4 pt-5 flex flex-col gap-4">

        <div class="flex flex-col gap-1 p-3 rounded-xl" style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
          <div class="text-xs text-gray-400">{{ $t('transfers.destinationLabel') }}</div>
          <div class="text-xs text-gray-500">{{ $t('common.location') }}</div>
          <div class="text-sm font-semibold text-gray-100">{{ transferDestinationLocationName() ?? '—' }}</div>
          <div class="text-xs text-gray-500">{{ $t('common.warehouse') }}</div>
          <div class="text-sm font-semibold text-gray-100">{{ transfer.destination_warehouse?.name ?? '—' }}</div>
          <div v-if="transfer.destination_area?.name" class="text-xs text-gray-400">→ {{ transfer.destination_area.name }}</div>
          <div v-else class="text-xs text-yellow-500/70">{{ $t('transfers.areaNotSet') }}</div>
          <div v-if="transfer.notes" class="text-xs text-gray-400 mt-1">{{ transfer.notes }}</div>
        </div>

        <div class="text-sm font-semibold text-gray-200">{{ $t('transfers.itemsLabel', { n: transfer.lines.length }) }}</div>

        <div class="p-3 rounded-xl" style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
          <BaseFormField
            :label="$t('transfers.defaultReceiveAreaLabel')"
            required
            :hint="$t('transfers.receiveAreaHint')"
            class="!mb-0"
          >
            <BaseSelect
              :model-value="receiveArea"
              density="compact"
              @update:model-value="(v) => { receiveArea = v; applyDefaultAreaToLines() }"
            >
              <option :value="null" disabled>{{ $t('transfers.selectDefaultArea') }}</option>
              <option v-for="a in receiveAreas" :key="a.id" :value="a.id">{{ a.name }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>

        <div v-if="receiveArea" class="p-3 rounded-xl" style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
          <BaseFormField :label="$t('transfers.scanToReceive')" :error="scanError || ''" class="!mb-0">
            <BaseInput
              ref="scanInputEl"
              v-model="scanCode"
              :disabled="saving || allReceived"
              :placeholder="$t('transfers.scanPlaceholder')"
              @keydown.enter.prevent="receiveScannedItem"
            />
          </BaseFormField>
        </div>

        <ul class="space-y-2">
          <li v-for="line in transfer.lines" :key="line.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
            :style="line.status === 2 ? 'background:#0d2017; border:1px solid rgba(74,222,128,0.15);' : 'background:#0f1720; border:1px solid rgba(255,255,255,0.06);'">
            <!-- Received indicator -->
            <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              :style="line.status === 2 ? 'background:#15803d; color:#fff;' : 'background:#374151; color:#9ca3af;'">
              <svg v-if="line.status === 2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-100 truncate">{{ line.item_metadata?.item?.name ?? $t('common.unknown') }}</div>
              <div class="text-xs text-gray-400">{{ line.item_metadata?.assetTag ?? line.item_metadata?.serial_number }}
                <span v-if="line.source_area?.name"> · {{ $t('transfers.fromArea', { area: line.source_area.name }) }}</span>
              </div>
            </div>
            <template v-if="line.status !== 2">
              <BaseFormField density="compact" class="!mb-0 shrink-0 max-w-[130px]">
                <BaseSelect
                  v-model="lineReceiveAreas[line.id]"
                  density="compact"
                  :aria-label="$t('transfers.useDefault')"
                >
                  <option :value="null">{{ $t('transfers.useDefault') }}</option>
                  <option v-for="a in receiveAreas" :key="a.id" :value="a.id">{{ a.name }}</option>
                </BaseSelect>
              </BaseFormField>
              <button @click="doReceiveItem(line)" :disabled="saving"
                class="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                style="background:#15803d; color:#fff;">
                {{ $t('transfers.receiveBtn') }}
              </button>
            </template>
            <span v-else class="text-xs text-green-400 font-medium shrink-0">{{ $t('transfers.received') }}</span>
          </li>
        </ul>

        <div class="flex gap-3 mt-2">
          <button @click="doCancel" :disabled="saving"
            class="flex-1 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            style="background:#1f2937; color:#f87171;">
            {{ $t('transfers.cancelTransfer') }}
          </button>
          <div class="flex-1 flex flex-col gap-2">
            <button @click="doReceiveAll" :disabled="saving || allReceived || !receiveArea"
              class="w-full py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
              style="background:#15803d; color:#fff;">
              {{ saving ? $t('transfers.receiving') : $t('transfers.receiveAll') }}
            </button>
          </div>
        </div>

      </div>

      <!-- ── RECEIVED / CANCELLED ── -->
      <div v-else-if="transfer" class="px-4 pt-5 flex flex-col gap-4">

        <div class="flex flex-col gap-1 p-3 rounded-xl" style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs text-gray-400">{{ $t('transfers.destinationLabel') }}</div>
              <div class="text-xs text-gray-500">{{ $t('common.location') }}</div>
              <div class="text-sm font-semibold text-gray-100">{{ transferDestinationLocationName() ?? '—' }}</div>
              <div class="text-xs text-gray-500">{{ $t('common.warehouse') }}</div>
              <div class="text-sm font-semibold text-gray-100">{{ transfer.destination_warehouse?.name ?? '—' }}</div>
              <div v-if="transfer.destination_area?.name" class="text-xs text-gray-400">→ {{ transfer.destination_area.name }}</div>
            </div>
            <span class="text-xs font-semibold px-2 py-1 rounded-lg" :style="statusBadgeStyle(transfer.status)">
              {{ transferStatusLabel(transfer.status, transfer.status_display) }}
            </span>
          </div>
          <div v-if="transfer.received_at" class="text-xs text-gray-400 mt-1">{{ $t('transfers.receivedAt', { date: formatDate(transfer.received_at) }) }}</div>
          <div v-if="transfer.notes" class="text-xs text-gray-400">{{ transfer.notes }}</div>
        </div>

        <div class="text-sm font-semibold text-gray-200">{{ $t('transfers.itemsLabel', { n: transfer.lines.length }) }}</div>

        <ul class="space-y-2">
          <li v-for="line in transfer.lines" :key="line.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-xl"
            style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);">
            <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              :style="line.status === 2 ? 'background:#15803d; color:#fff;' : 'background:#374151; color:#9ca3af;'">
              <svg v-if="line.status === 2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-100 truncate">{{ line.item_metadata?.item?.name ?? $t('common.unknown') }}</div>
              <div class="text-xs text-gray-400">{{ line.item_metadata?.assetTag ?? line.item_metadata?.serial_number }}
                <span v-if="line.source_area?.name"> · {{ $t('transfers.fromArea', { area: line.source_area.name }) }}</span>
              </div>
            </div>
            <span class="text-xs shrink-0" :style="line.status === 2 ? 'color:#4ade80;' : 'color:#6b7280;'">{{ transferLineStatusLabel(line.status, line.status_display) }}</span>
          </li>
        </ul>

      </div>

    </template>

  </div>
</template>

<script setup>
import { useStatusLabels } from '@/composables/useStatusLabels.js'
const { transferStatusLabel, transferLineStatusLabel } = useStatusLabels()

import BaseTaskHelp from '@/components/base/BaseTaskHelp.vue'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import { ref, computed, onMounted, watch } from 'vue'
import SearchField from '@/components/search/SearchField.vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import {
  getTransfer, createTransfer, updateTransfer,
  addTransferItems, removeTransferItem,
  dispatchTransfer, receiveTransfer, receiveTransferItem, cancelTransfer,
  searchItemMetadata, getAreas, getWarehouses, getLocations,
} from '@/api/api.js'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { t } = useI18n()

const STATUS = { DRAFT: 0, DISPATCHED: 1, PARTIALLY_RECEIVED: 2, RECEIVED: 3, CANCELLED: 4 }

const isNew = computed(() => route.params.transferId === 'new')
const loading = ref(false)
const loadError = ref(null)
const saving = ref(false)
const transfer = ref(null)
const areas = ref([])
const warehouses = ref([])
const locations = ref([])

const form = ref({ name: '', destination_warehouse: null, destination_area: null, notes: '' })

// Receive state
const receiveArea = ref(null)           // area for bulk receive
const lineReceiveAreas = ref({})        // line.id → area.id for per-item receive
const scanCode = ref('')
const scanError = ref('')
const scanInputEl = ref(null)

// Item search (used both for new draft and existing draft)
const itemQuery = ref('')
const itemSuggestions = ref([])
const pendingItems = ref([]) // only used when isNew

const mappedItemSuggestions = computed(() =>
  itemSuggestions.value.map(inst => ({
    id: inst.id,
    label: inst.item?.name ?? t('common.unknown'),
    sublabel: [inst.assetTag ?? inst.serial_number, inst.area?.name].filter(Boolean).join(' · '),
    meta: inst,
  }))
)

const allReceived = computed(() =>
  transfer.value?.lines?.every(l => l.status === 2) ?? false
)

// Areas within the currently selected destination warehouse (draft form)
const draftAreas = computed(() =>
  form.value.destination_warehouse
    ? areas.value.filter(a => a.warehouse === form.value.destination_warehouse)
    : []
)

// Areas within the transfer's destination warehouse (receiving)
const receiveAreas = computed(() => {
  const wId = transfer.value?.destination_warehouse?.id
  return wId ? areas.value.filter(a => a.warehouse === wId) : areas.value
})

function statusBadgeStyle(status) {
  const map = {
    0: 'background:#1c2a3a; color:#93c5fd;',
    1: 'background:#1c2a20; color:#86efac;',
    2: 'background:#2a2a1c; color:#fde68a;',
    3: 'background:#1a2a1a; color:#4ade80;',
    4: 'background:#2a1c1c; color:#f87171;',
  }
  return map[status] ?? 'background:#1f2937; color:#9ca3af;'
}

function formatDate(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function extractApiErrorMessage(error, fallback) {
  const data = error?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (typeof data.error === 'string') return data.error
  if (typeof data.detail === 'string') return data.detail

  const firstValue = Object.values(data)[0]
  if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0])
  if (typeof firstValue === 'string') return firstValue
  return fallback
}

function applyDefaultAreaToLines() {
  if (!receiveArea.value || !transfer.value?.lines?.length) return
  const next = { ...lineReceiveAreas.value }
  transfer.value.lines.forEach(line => {
    if (line.status !== 2) next[line.id] = receiveArea.value
  })
  lineReceiveAreas.value = next
  scanError.value = ''
}

async function doItemSearch(q) {
  if (!q) { itemSuggestions.value = []; return }
  try {
    const res = await searchItemMetadata(q)
    const results = res.results ?? res ?? []
    const existingIds = new Set([
      ...pendingItems.value.map(i => i.id),
      ...(transfer.value?.lines?.map(l => l.item_metadata?.id) ?? []),
    ])
    itemSuggestions.value = results.filter(i => !existingIds.has(i.id))
  } catch { /* ignore */ }
}

function addInstance(inst) {
  if (isNew.value) {
    if (!pendingItems.value.find(i => i.id === inst.id)) {
      pendingItems.value.push(inst)
    }
    itemQuery.value = ''
    itemSuggestions.value = []
  } else {
    // Add directly to existing draft
    addItemToExistingDraft(inst)
  }
}

async function addItemToExistingDraft(inst) {
  saving.value = true
  try {
    await addTransferItems(transfer.value.id, [inst.id])
    transfer.value = await getTransfer(transfer.value.id)
    itemQuery.value = ''
    itemSuggestions.value = []
    toast.success(t('transfers.itemAdded'))
  } catch {
    toast.error(t('transfers.addFailed'))
  } finally {
    saving.value = false
  }
}

function removePending(id) {
  pendingItems.value = pendingItems.value.filter(i => i.id !== id)
}

async function removeLine(lineId) {
  saving.value = true
  try {
    await removeTransferItem(transfer.value.id, lineId)
    transfer.value = await getTransfer(transfer.value.id)
    toast.success(t('transfers.itemRemoved'))
  } catch {
    toast.error(t('transfers.removeFailed'))
  } finally {
    saving.value = false
  }
}

async function createDraft() {
  saving.value = true
  try {
    const payload = {
      name: form.value.name,
      destination_warehouse: form.value.destination_warehouse,
      item_metadata_ids: pendingItems.value.map(i => i.id),
      notes: form.value.notes,
    }
    if (form.value.destination_area) payload.destination_area = form.value.destination_area
    const created = await createTransfer(payload)
    toast.success(t('transfers.created'))
    await router.replace(`/transfers/${created.id}`)
  } catch {
    toast.error(t('transfers.createFailed'))
  } finally {
    saving.value = false
  }
}

async function saveDraft() {
  saving.value = true
  try {
    const payload = {
      name: form.value.name,
      destination_warehouse: form.value.destination_warehouse,
      notes: form.value.notes,
      destination_area: form.value.destination_area || null,
    }
    const updated = await updateTransfer(transfer.value.id, payload)
    transfer.value = { ...transfer.value, ...updated }
    toast.success(t('transfers.saved'))
  } catch {
    toast.error(t('transfers.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function doDispatch() {
  if (!confirm(t('transfers.dispatchConfirm'))) return
  saving.value = true
  try {
    transfer.value = await dispatchTransfer(transfer.value.id)
    toast.success(t('transfers.dispatched'))
  } catch {
    toast.error(t('transfers.dispatchFailed'))
  } finally {
    saving.value = false
  }
}

async function doReceiveAll() {
  if (!confirm(t('transfers.receiveAllConfirm'))) return

  const effectiveArea = receiveArea.value ?? transfer.value?.destination_area?.id ?? null
  if (!effectiveArea) {
    toast.error(t('transfers.noReceiveAreaError'))
    return
  }

  saving.value = true
  try {
    transfer.value = await receiveTransfer(transfer.value.id, effectiveArea)
    toast.success(t('transfers.allReceived'))
  } catch (error) {
    toast.error(extractApiErrorMessage(error, t('transfers.receiveFailed')))
  } finally {
    saving.value = false
  }
}

async function doReceiveItem(line) {
  const effectiveArea = lineReceiveAreas.value[line.id] ?? receiveArea.value ?? transfer.value?.destination_area?.id ?? null
  if (!effectiveArea) {
    const msg = t('transfers.noReceiveAreaError')
    toast.error(msg)
    return { ok: false, error: msg }
  }

  saving.value = true
  try {
    transfer.value = await receiveTransferItem(transfer.value.id, line.id, '', effectiveArea)
    toast.success(t('transfers.itemReceived'))
    return { ok: true }
  } catch (error) {
    const msg = extractApiErrorMessage(error, t('transfers.receiveFailed'))
    toast.error(msg)
    return { ok: false, error: msg }
  } finally {
    saving.value = false
  }
}

function normalizeScanValue(value) {
  return String(value ?? '').trim().toLowerCase()
}

function findPendingLineByScan(code) {
  const scanned = normalizeScanValue(code)
  if (!scanned) return null

  const matches = (transfer.value?.lines ?? []).filter(line => {
    if (line.status === 2) return false
    const assetTag = normalizeScanValue(line.item_metadata?.assetTag)
    const serialNumber = normalizeScanValue(line.item_metadata?.serial_number)
    return assetTag === scanned || serialNumber === scanned
  })

  if (matches.length !== 1) return null
  return matches[0]
}

async function receiveScannedItem() {
  const scanned = scanCode.value.trim()
  if (!receiveArea.value) {
    scanError.value = t('transfers.selectAreaFirst')
    return
  }
  if (!scanned || saving.value || allReceived.value) return

  scanError.value = ''
  const line = findPendingLineByScan(scanned)
  if (!line) {
    scanError.value = t('transfers.itemNotFound', { code: scanned })
    return
  }

  const result = await doReceiveItem(line)
  if (result?.ok) {
    scanCode.value = ''
    scanError.value = ''
  } else {
    scanError.value = result?.error ?? t('transfers.receiveScanFailed', { code: scanned })
  }
  scanInputEl.value?.focus()
}

async function doCancel() {
  if (!confirm(t('transfers.cancelConfirm'))) return
  saving.value = true
  try {
    transfer.value = await cancelTransfer(transfer.value.id)
    toast.success(t('transfers.cancelled'))
  } catch {
    toast.error(t('transfers.cancelFailed'))
  } finally {
    saving.value = false
  }
}

function warehouseLabel(w) {
  const loc = locations.value.find(l => l.id === w.location)
  return loc ? `${w.name} — ${loc.name}` : w.name
}

function transferDestinationLocationName() {
  const location = transfer.value?.destination_warehouse?.location
  if (!location) return null
  if (typeof location === 'object') return location.name ?? null
  return locations.value.find(l => l.id === location)?.name ?? null
}

async function load() {
  // Always load areas, warehouses, and locations for selectors
  try {
    const [aRes, wRes, lRes] = await Promise.all([getAreas(), getWarehouses(), getLocations()])
    areas.value = aRes.results ?? aRes ?? []
    warehouses.value = wRes.results ?? wRes ?? []
    locations.value = lRes.results ?? lRes ?? []
  } catch { /* ignore */ }

  if (isNew.value) {
    transfer.value = null
    loadError.value = null
    return
  }

  loading.value = true
  loadError.value = null
  try {
    const data = await getTransfer(route.params.transferId)
    transfer.value = data
    receiveArea.value = data.destination_area?.id ?? null
    lineReceiveAreas.value = {}
    applyDefaultAreaToLines()
    form.value = {
      name: data.name,
      destination_warehouse: data.destination_warehouse?.id ?? null,
      destination_area: data.destination_area?.id ?? null,
      notes: data.notes ?? '',
    }
  } catch {
    loadError.value = t('transfers.loadTransferFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => route.params.transferId, load)


</script>
